import { Stack, Typography, useTheme } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import {
	getRoute,
	type RouteObject,
} from "web_myplann/Infrastructure/Utils/UrlUtils";
import { HeaderBase } from "web_myplann/Shared/Header/HeaderBase";
import { HeaderIconButton } from "web_myplann/Shared/Header/HeaderIconButton";
import { MyPlannNavigationHeaderActionSlot } from "web_myplann/Shared/Header/MyPlannNavigationHeaderActionSlot";
import { useNavigateBackOrDefault } from "web_myplann/Shared/Hooks/useNavigateBackOrDefault";

type Props = {
	title: React.ReactNode;
	defaultRoute?: RouteObject | string;
	endComponent?: React.ReactNode;
	bgColor?: string;
	iconColor?: string;
	alwaysUseDefaultRoute?: boolean;
	replaceOnBack?: boolean;
};

export const MyPlannNavigationHeader = ({
	title,
	defaultRoute,
	endComponent,
	bgColor,
	iconColor,
	alwaysUseDefaultRoute = false,
	replaceOnBack = false,
}: Props) => {
	const theme = useTheme();
	const resolvedDefaultRoute = defaultRoute
		? typeof defaultRoute === "string"
			? defaultRoute
			: getRoute(defaultRoute)
		: undefined;
	const navigateBack = useNavigateBackOrDefault(resolvedDefaultRoute, {
		alwaysUseDefaultRoute,
		replaceOnBack,
	});

	return (
		<HeaderBase bgColor={bgColor} contentPadding={{ x: 1.6, y: 1.6 }}>
			<Stack direction="row" alignItems="center" width="100%">
				<MyPlannNavigationHeaderActionSlot align="flex-start">
					{defaultRoute && (
						<HeaderIconButton onClick={navigateBack}>
							<ArrowLeft
								size={theme.iconSize.md}
								color={iconColor ?? theme.palette.grey[900]}
							/>
						</HeaderIconButton>
					)}
				</MyPlannNavigationHeaderActionSlot>

				<Stack flex={1} minWidth={0} alignItems="center" textAlign="center">
					{typeof title === "string" ? (
						<Typography
							variant="h3"
							textOverflow="ellipsis"
							overflow="hidden"
							whiteSpace="nowrap"
							maxWidth="100%"
						>
							{title}
						</Typography>
					) : (
						title
					)}
				</Stack>

				<MyPlannNavigationHeaderActionSlot align="flex-end">
					{endComponent}
				</MyPlannNavigationHeaderActionSlot>
			</Stack>
		</HeaderBase>
	);
};
